import KlCreateGeometryOnMap from './components/kl-create-geometry-on-map/kl-create-geometry-on-map.vue';
import { IFlatGeometry } from './components/kl-create-geometry-on-map/kl-create-geometry-on-map';
import {useSettingsStore} from '@/app/settings/settings-store';
import {
    Geometry,
    UpdateKlipZoneInput,
    CreateKlipZoneInput,
    GeometryInput,
    IGeometryInput,
    IGeometry
} from '@/api/klip-api.proxy';
import { EditSubOrganisation } from '../../../../kl-organisation-routes';
import {computed, defineComponent, onMounted, ref, watch} from 'vue';
import {useRouter} from '@/router/router-vue3';
import {useKlipApiProxy} from '@/plugins/proxy-client';
import {componentRef} from '@/app/shared/helpers/vue-typescript-util';
import {useForm} from 'vee-validate';
import {
    klValidateRules,
    klValidationFields,
    ValidationFieldsType
} from '@/app/shared/components/kl-form-fields/kl-field-validation-util';
import {useKlModalEventsUtil} from '@/app/shared/helpers/kl-modal-events-util';
import DateUtil from '@/app/shared/helpers/date-util';
import {first, isEmpty} from 'lodash-es';
import * as yup from 'yup';
import {toTypedSchema} from '@vee-validate/yup';

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlUnaCreateEditZone',
    components: {
        KlCreateGeometryOnMap,
    },
    props: {
        zoneId: { type: String, default: '' },
        organisationId: { type: String, default: '' },
    },
    setup(props) {

        const zoneNameValidationRegex = /^[a-zA-Z0-9\-_ .+!()&ö/:,è|'@\s]*$/;
        const namespaceRegex = /^[0-9a-zA-Z-_]{5,50}$/;
        const schema = yup.object({
            zoneId: yup.string().notRequired(),
            initialName: yup.string().notRequired(),
            name: yup.string().required().max(90).matches(zoneNameValidationRegex).kl_uniqueZoneName('zoneId', 'initialName').label('Naam zone'),
            mailDestination: yup.string().required().kl_validKlipEmail().label('Stuur planaanvragen naar e-mail'),
            mailSettings: yup.object(),
            namespace: yup.string().required().matches(namespaceRegex).kl_uniqueNamespace('zoneId').label('Namespace'),
            contactName: yup.string().required().max(100).label('Naam contact'),
            contactPhone: yup.string().required().kl_phone().label('Telefoon'),
            contactEmail: yup.string().required().kl_validKlipEmail().label('Email'),
            contactMessage: yup.string().notRequired().max(400).label('Boodschap voor de planaanvrager'),
            contactPhoneEmergency: yup.string().required().max(20).kl_phone_with_112().label('Telefoon in geval van nood'),

            geometries: yup.array(), // only to check pristine
        });

        const form = useForm({
            validationSchema: toTypedSchema(schema),
            initialValues: {
                zoneId: props.zoneId || '',
                initialName: '',
                geometries: [],
            }
        });
        const fields: ValidationFieldsType<typeof schema> = klValidationFields<typeof schema>(schema);


        const createGeometryOnMap = ref<InstanceType<typeof KlCreateGeometryOnMap>>(null);

        const router = useRouter();

        const removeZoneModalEvents = useKlModalEventsUtil('remove-zone');
        const removeGeometryModalEvents = useKlModalEventsUtil('remove-geometry');

        const namespaceUrl: string = import.meta.env.VITE_LINKS_NAMESPACE;
        const overlayUrl: string = import.meta.env.VITE_LINKS_OVERLAY;
        const helpdeskMail: string = import.meta.env.VITE_EMAIL_HELPDESK;

        const hasValidationErrors = ref<boolean>(false);
        const hasGeometryValidationErrors = ref<boolean>(false);


        const disableSubmit = computed((): boolean => {
            const invalid = !form.meta.value.valid;
            const pristine = !form.meta.value.dirty;

            // never disable the submit in createMode
            if (!editMode.value) {
                return false;
            }

            if (invalid || pristine || !hasGeometry.value) {
                return true;
            }
            return false;
        });

        const name = fields.name.getValue<string>();
        const initialName = fields.initialName.getValue<string>();
        const mailDestination = fields.mailDestination.getValue<string>();
        const namespace = fields.namespace.getValue<string>();
        const contactName = fields.contactName.getValue<string>();
        const contactPhone = fields.contactPhone.getValue<string>();
        const contactEmail = fields.contactEmail.getValue<string>();
        const contactMessage = fields.contactMessage.getValue<string>();
        const contactPhoneEmergency = fields.contactPhoneEmergency.getValue<string>();
        const geometries = fields.geometries.getValue<IGeometry[]>();

        const mailSettings = ref({
            confirmation: false,
            confirmationExampleUri: '',
            reminder1DayToRespond: false,
            reminder1DayToRespondExampleUri: '',
            uploadImklSuccess: false,
            uploadImklSuccessExampleUri: '',
            uploadImklFailed: false,
            uploadImklFailedExampleUri: '',
            respondedNotInvolved: false,
            respondedNotInvolvedExampleUri: ''
        });

        // update form.meta.value.dirty flag
        watch(mailSettings, (newValue) => { fields.mailSettings.value.value = newValue; }, { deep: true, flush: 'sync' });


        const isLoading = ref<boolean>(true);

        const isSending = ref<boolean>(false);
        const deletingZone = ref<boolean>(false);
        const deletingGeometry = ref<boolean>(false);
        const addingNewGeometry = ref<boolean>(false);
        const geometryToBeDeleted = ref<Geometry>();
        const unaDisplayName = ref<string>('');
        const unaId = ref<number>();

        const _deleteNamespaceZone = (deleteNamespace: { id: string, organisationId: string }) => useSettingsStore().deleteNamespaceZone(deleteNamespace);

        const routeEditOrganinsation = computed(() => {
            return EditSubOrganisation;
        });

        const editMode = computed((): boolean => {
            return !!props.zoneId;
        });

        const createMode = computed((): boolean => {
            return !props.zoneId;
        });

        const canAddGeometry = computed((): boolean => {
            return editMode.value && !addingNewGeometry.value;
        });

        const hasGeometry = computed(() => {
            return !isEmpty(geometries.value);
        });

        const canRevert = computed((): boolean => {
            return !!geometries.value.length;
        });

        const title = computed((): string => {
            return editMode.value ? 'Bewerk zone' : 'Nieuwe zone';
        });

        const submitButtonLabel = computed((): string => {
            return editMode.value ? 'Bewaar veranderingen' : 'Zone toevoegen';
        });

        const getGeometryUniqueKey = (geometry: IGeometry): string => {
            return `geometry-${geometry?.id}-${geometry?.blobName}-${geometry?.wkt?.length}`;
        }

        const geometryUpdate = (newGeometry: IFlatGeometry) => {
            if (!editMode.value) {
                // er kan maar 1 geometry zijn in create mode
                geometries.value = [];
            }

            // from geometryUploadFinished
            geometries.value.push({
                activationDate: newGeometry.activationDate,
                version: newGeometry.version,
                precision: newGeometry.precision,
                lastModificationDate: newGeometry.lastModificationDate,
                reference: newGeometry.reference,
                wkt: newGeometry.wkt,
            });
            addingNewGeometry.value = false;
        }

        const geometryBeforeDelete = (newGeometry: Geometry) => {
            geometryToBeDeleted.value = newGeometry;
            removeGeometryModalEvents.triggerModalToggle();
        }

        const geometryDelete = () => {
            deletingGeometry.value = true;

            // create mode = only 1 geometry possible
            if (!editMode.value) {
                geometries.value = [];
            }
            else {
                // edit mode
                const index = geometries.value.findIndex((g) => g.id === geometryToBeDeleted.value.id);
                if (index > -1) {
                    geometries.value.splice(index, 1);
                }
            }

            deletingGeometry.value = false;
        }

        const _submit = async () => {

            isSending.value = true;

            hasValidationErrors.value = false;
            hasGeometryValidationErrors.value = false;

            const createOnMap = componentRef<InstanceType<typeof KlCreateGeometryOnMap>>(createGeometryOnMap).value;

            // create-flow: auto-submit geometry
            if (!editMode.value && !!createOnMap) {
                const validateGeometryResult = await createOnMap.onSubmit();
                if (!validateGeometryResult) {
                    hasGeometryValidationErrors.value = true;
                    isSending.value = false;
                    return;
                }
            }

            // Double check (should not be possible)
            if (!hasGeometry.value) {
                hasGeometryValidationErrors.value = true;
                isSending.value = false;
                return;
            }

            const data = {
                organisationId: props.organisationId,
                alarmCentralTelephone: contactPhoneEmergency.value,
                contactName: contactName.value,
                contactEmail: contactEmail.value,
                contactTelephone: contactPhone.value,
                extraInformation: contactMessage.value,
                mapRequestEmail: mailDestination.value,
                name: name.value,
                namespace: namespace.value,
                confirmation: mailSettings.value.confirmation,
                reminder1DayToRespond: mailSettings.value.reminder1DayToRespond,
                respondedNotInvolved: mailSettings.value.respondedNotInvolved,
                uploadImklFailed: mailSettings.value.uploadImklFailed,
                uploadImklSuccess: mailSettings.value.uploadImklSuccess
            } as UpdateKlipZoneInput | CreateKlipZoneInput;

            if (editMode.value) {
                const fullData = _addGeometriesToUpdateKlipZone(data);
                await useSettingsStore().updateKlipNamespaceZone(fullData);
            }
            else {
                const fullData = _addGeometriesToCreateKlipZone(data);
                await useSettingsStore().createKlipNamespaceZone(fullData);
            }

            isSending.value = false;
            addingNewGeometry.value = false;

            await router.replace({
                name: routeEditOrganinsation.value.name,
                params: {
                    organisationId: props.organisationId,
                }
            });
        }

        const cancelNewGeometry = () => {
            addingNewGeometry.value = false;
        }

        const addNewGeometry = () => {
            addingNewGeometry.value = true;
        }

        const deleteZone = () => {
            deletingZone.value = true;

            _deleteNamespaceZone({ id: props.zoneId, organisationId: props.organisationId}).then(async () => {
                removeZoneModalEvents.triggerModalToggle();
                await router.replace({
                    name: routeEditOrganinsation.value.name,
                    params: {
                        organisationId: props.organisationId,
                        removedItem: props.zoneId
                    },
                });
            });
        }

        const _addGeometriesToCreateKlipZone = (createZoneInput: CreateKlipZoneInput): CreateKlipZoneInput => {
            const geometry = geometries.value[0];
            const klipData = CreateKlipZoneInput.fromJS({
                ...createZoneInput,
                geometryActivationDate: DateUtil.formatForApi(geometry.activationDate, 'YYYY-MM-DD 00:00:00'),
                geometryWkt: geometry.wkt,
                geometryLastModificationDate: DateUtil.formatForApi(geometry.lastModificationDate, 'YYYY-MM-DD 00:00:00'),
                geometryPrecision: geometry.precision,
                geometryReference: geometry.reference,
                geometryVersion: geometry.version,
            });
            return klipData;
        }

        const _addGeometriesToUpdateKlipZone = (updateZoneInput: UpdateKlipZoneInput): UpdateKlipZoneInput => {
            const klipData = UpdateKlipZoneInput.fromJS({
                ...updateZoneInput,
                id: props.zoneId,
                geometries: geometries.value
                    //.filter((g) => g.isDeleted === false)
                    .map((geometry: IGeometry): GeometryInput => {
                        return GeometryInput.fromJS({
                            activationDate: DateUtil.formatForApi(geometry.activationDate, 'YYYY-MM-DD 00:00:00'),
                            id: geometry.id,
                            wkt: geometry.wkt,
                            isActive: geometry.isActive,
                            lastModificationDate: DateUtil.formatForApi(geometry.lastModificationDate, 'YYYY-MM-DD 00:00:00'),
                            precision: geometry.precision,
                            reference: geometry.reference,
                            version: geometry.version,
                        });
                    }),
                });
            return klipData;
        }

        onMounted(() => {
            addingNewGeometry.value = !!createMode.value;
        });

        const _onZoneIdChange = (newZoneId: string) => {
            if (!!newZoneId) {
                isLoading.value = true;
                useKlipApiProxy().unaSettings_GetUnaZoneEditView(newZoneId, props.organisationId)
                    .then((response) => {
                        initialName.value = response.result.zoneName;
                        name.value = response.result.zoneName;
                        unaDisplayName.value = response.result.unaName;
                        mailDestination.value = response.result.mapRequestEmail;
                        namespace.value = response.result.namespace;
                        geometries.value = response.result.geometries;
                        unaId.value = response.result.unaId;
                        contactName.value = response.result.contactName;
                        contactPhone.value = response.result.contactTelephone
                        contactEmail.value = response.result.contactEmail;
                        contactMessage.value = response.result.extraInformation;
                        contactPhoneEmergency.value = response.result.alarmCentralTelephone;

                        mailSettings.value = {
                            confirmation: response.result.confirmation,
                            confirmationExampleUri: response.result.confirmationExampleUri,
                            reminder1DayToRespond: response.result.reminder1DayToRespond,
                            reminder1DayToRespondExampleUri: response.result.reminder1DayToRespondExampleUri,
                            uploadImklFailed: response.result.uploadImklFailed,
                            uploadImklFailedExampleUri: response.result.uploadImklFailedExampleUri,
                            uploadImklSuccess: response.result.uploadImklSuccess,
                            uploadImklSuccessExampleUri: response.result.uploadImklSuccessExampleUri,
                            respondedNotInvolved: response.result.respondedNotInvolved,
                            respondedNotInvolvedExampleUri: response.result.respondedNotInvolvedExampleUri
                        };
                        //geometries.value.forEach((g) => g.isDeleted = false);

                        form.resetForm({ values: form.values });

                        isLoading.value = false;
                    });
            } else {
                isLoading.value = true;
                useKlipApiProxy().unaSettings_GetUnaZoneCreateView()
                    .then((response) => {
                        mailSettings.value = {
                            confirmation: true,
                            confirmationExampleUri: response.result.confirmationExampleUri,
                            reminder1DayToRespond: false,
                            reminder1DayToRespondExampleUri: response.result.reminder1DayToRespondExampleUri,
                            uploadImklFailed: false,
                            uploadImklFailedExampleUri: response.result.uploadImklFailedExampleUri,
                            uploadImklSuccess: false,
                            uploadImklSuccessExampleUri: response.result.uploadImklSuccessExampleUri,
                            respondedNotInvolved: false,
                            respondedNotInvolvedExampleUri: response.result.respondedNotInvolvedExampleUri
                        };
                        isLoading.value = false;
                    });
            }
        }

        watch(
            geometries,
            (newGeometries: IGeometry[]) => {
                if (!newGeometries.length) {
                    addingNewGeometry.value = true;
                }
            },
            { immediate: false, deep: true });

        watch(
            () => props.zoneId,
            _onZoneIdChange,
            { immediate: true });


        const _onInvalidSubmit = (submittedValues) => {
            hasValidationErrors.value = true;
        }

        const onSubmit = form.handleSubmit(_submit, _onInvalidSubmit);

        return {
            namespaceUrl,
            overlayUrl,
            helpdeskMail,

            createGeometryOnMap,

            isLoading,

            name,
            mailDestination,
            namespace,
            mailSettings,
            contactName,
            contactPhone,
            contactEmail,
            contactMessage,
            contactPhoneEmergency,

            isSending,
            deletingZone,
            deletingGeometry,
            addingNewGeometry,
            geometries,
            geometryToBeDeleted,
            unaDisplayName,

            routeEditOrganinsation,
            editMode,
            createMode,
            canAddGeometry,
            canRevert,
            title,
            submitButtonLabel,

            hasGeometry,
            hasValidationErrors,
            hasGeometryValidationErrors,

            geometryUpdate,
            geometryBeforeDelete,
            geometryDelete,
            cancelNewGeometry,
            addNewGeometry,
            deleteZone,

            onSubmit,
            disableSubmit,

            getGeometryUniqueKey,

            fields,
        }

    }
})
